import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { AlertTriangle, CheckCircle2, Info, X } from "lucide-react";

type ToastTone = "success" | "error" | "info";
type ToastInput = {
  tone: ToastTone;
  title: string;
  message: string;
  duration?: number;
};
type ToastRecord = ToastInput & { id: number; key: string };

const ToastContext = createContext<
  | {
      showToast: (toast: ToastInput) => void;
      dismissToast: (id: number) => void;
    }
  | undefined
>(undefined);

const ToastItem = ({
  toast,
  onDismiss,
}: {
  toast: ToastRecord;
  onDismiss: (id: number) => void;
}) => {
  useEffect(() => {
    const timer = window.setTimeout(
      () => onDismiss(toast.id),
      toast.duration ?? (toast.tone === "error" ? 7000 : 4500),
    );
    return () => window.clearTimeout(timer);
  }, [onDismiss, toast.duration, toast.id, toast.tone]);

  const Icon =
    toast.tone === "success"
      ? CheckCircle2
      : toast.tone === "error"
        ? AlertTriangle
        : Info;

  return (
    <article
      className={`toast toast-${toast.tone}`}
      role={toast.tone === "error" ? "alert" : "status"}
    >
      <span className="toast-icon" aria-hidden="true">
        <Icon size={18} />
      </span>
      <span className="toast-copy">
        <strong>{toast.title}</strong>
        <span>{toast.message}</span>
      </span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        aria-label="Dismiss notification"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </article>
  );
};

export const ToastProvider = ({ children }: { children: ReactNode }) => {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  const nextId = useRef(1);

  const dismissToast = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((input: ToastInput) => {
    const key = `${input.tone}:${input.title}:${input.message}`;
    setToasts((current) => {
      if (current.some((toast) => toast.key === key)) return current;
      return [
        ...current.slice(-3),
        { ...input, id: nextId.current++, key },
      ];
    });
  }, []);

  const value = useMemo(
    () => ({ showToast, dismissToast }),
    [dismissToast, showToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="toast-viewport" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismissToast} />
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const value = useContext(ToastContext);
  if (!value) throw new Error("useToast must be used inside ToastProvider.");
  return value;
};

export const ToastNotice = ({
  tone,
  title,
  message,
}: ToastInput) => {
  const { showToast } = useToast();
  useEffect(() => {
    showToast({ tone, title, message });
  }, [message, showToast, title, tone]);
  return null;
};
